import { CallBadge } from "@/components/shared/CallBadge";
import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { SEOHead } from "@/components/shared/SEOHead";
import { PublicLayout } from "@/components/shared/PublicLayout";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { getLocations, getComingSoonLocations, getPageContent } from "@/lib/api";
import { motion } from "framer-motion";
import { MapPin, ChevronRight, Clock, Map, Search, Navigation, Loader2, ChevronDown, ChevronUp, Sparkles } from "lucide-react";
import { MapContainer, TileLayer, Marker, Popup, Circle, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png",
  iconUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png",
  shadowUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png",
});

const tealIcon = new L.Icon({
  iconUrl: "https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-green.png",
  shadowUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png",
  iconSize: [25, 41], iconAnchor: [12, 41], popupAnchor: [1, -34], shadowSize: [41, 41],
});

const redIcon = new L.Icon({
  iconUrl: "https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-red.png",
  shadowUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png",
  iconSize: [25, 41], iconAnchor: [12, 41], popupAnchor: [1, -34], shadowSize: [41, 41],
});

const orangeIcon = new L.Icon({
  iconUrl: "https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-orange.png",
  shadowUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png",
  iconSize: [25, 41], iconAnchor: [12, 41], popupAnchor: [1, -34], shadowSize: [41, 41],
});

function getSection(sections, name, fallback = {}) {
  const found = sections.find((s) => s.section_name === name && s.active !== false);
  if (!found) return fallback;
  return { ...fallback, ...found, metadata: { ...(fallback.metadata || {}), ...(found.metadata || {}) } };
}

function FitBounds({ locations, searchCoords }) {
  const map = useMap();
  useEffect(() => {
    if (searchCoords) {
      map.invalidateSize();
      map.setView([searchCoords.lat, searchCoords.lng], 10);
    } else if (locations.length > 0) {
      const bounds = L.latLngBounds(locations.map((l) => [l.latitude, l.longitude]));
      setTimeout(() => {
        map.invalidateSize();
        map.fitBounds(bounds, { padding: [40, 40], maxZoom: 12 });
      }, 300);
    }
  }, [locations, searchCoords, map]);
  return null;
}

// Haversine distance in miles
function getDistanceMiles(lat1, lon1, lat2, lon2) {
  const R = 3959;
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLon = ((lon2 - lon1) * Math.PI) / 180;
  const a = Math.sin(dLat / 2) ** 2 + Math.cos((lat1 * Math.PI) / 180) * Math.cos((lat2 * Math.PI) / 180) * Math.sin(dLon / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

function getStoreStatus(hoursStr) {
  if (!hoursStr) return null;
  const now = new Date();
  const day = now.getDay(); // 0=Sun, 1=Mon...6=Sat
  const currentMinutes = now.getHours() * 60 + now.getMinutes();
  const dayNames = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];

  const parseTime = (t) => {
    const m = t.trim().match(/(\d{1,2}):(\d{2})\s*(AM|PM)/i);
    if (!m) return null;
    let h = parseInt(m[1]);
    if (m[3].toUpperCase() === "PM" && h !== 12) h += 12;
    if (m[3].toUpperCase() === "AM" && h === 12) h = 0;
    return h * 60 + parseInt(m[2]);
  };

  const formatTime = (mins) => {
    let h = Math.floor(mins / 60), m = mins % 60;
    const ampm = h >= 12 ? "PM" : "AM";
    h = h % 12 || 12;
    return `${h}:${m.toString().padStart(2, "0")} ${ampm}`;
  };

  const lines = hoursStr.split("\n").map((l) => l.trim()).filter(Boolean);
  const dayMap = { sun: 0, mon: 1, tue: 2, wed: 3, thu: 4, fri: 5, sat: 6, sunday: 0, monday: 1, tuesday: 2, wednesday: 3, thursday: 4, friday: 5, saturday: 6 };

  // Build schedule for all 7 days
  const schedule = Array(7).fill(null);

  for (const line of lines) {
    const match = line.match(/^(.+?):\s*(\d{1,2}:\d{2}\s*[AP]M)\s*[-–]\s*(\d{1,2}:\d{2}\s*[AP]M)/i);
    if (!match) continue;
    const dayPart = match[1].trim().toLowerCase();
    const open = parseTime(match[2]);
    const close = parseTime(match[3]);
    if (open === null || close === null) continue;

    const rangeParts = dayPart.split(/\s*[-–]\s*/);
    if (rangeParts.length === 2) {
      const startDay = dayMap[rangeParts[0].trim().slice(0, 3)];
      const endDay = dayMap[rangeParts[1].trim().slice(0, 3)];
      if (startDay !== undefined && endDay !== undefined) {
        for (let d = startDay; d !== (endDay + 1) % 7; d = (d + 1) % 7) {
          schedule[d] = { open, close };
        }
        schedule[endDay] = { open, close };
      }
    } else {
      const singleDay = dayMap[dayPart.slice(0, 3)];
      if (singleDay !== undefined) schedule[singleDay] = { open, close };
    }
  }

  const todaySchedule = schedule[day];
  const weekly = dayNames.map((name, i) => ({
    day: name,
    hours: schedule[i] ? `${formatTime(schedule[i].open)} - ${formatTime(schedule[i].close)}` : "Closed",
    isToday: i === day,
  }));

  if (!todaySchedule) return { isOpen: false, text: "Closed", statusWord: "Closed", nextOpen: "", weekly };

  if (currentMinutes >= todaySchedule.open && currentMinutes < todaySchedule.close) {
    return { isOpen: true, text: `Open until ${formatTime(todaySchedule.close)}`, statusWord: "Open", nextOpen: formatTime(todaySchedule.close), weekly };
  } else {
    return { isOpen: false, text: `Closed`, statusWord: "Closed", nextOpen: `Opens at ${formatTime(todaySchedule.open)}`, weekly };
  }
}

function StoreHours({ hours }) {
  const [expanded, setExpanded] = useState(false);
  const status = getStoreStatus(hours);
  if (!status) return null;

  return (
    <div className="relative" data-testid="store-hours">
      <button onClick={(e) => { e.preventDefault(); e.stopPropagation(); setExpanded(!expanded); }} className="flex items-center gap-2 text-sm font-medium w-full text-left">
        <Clock className={`w-4 h-4 flex-shrink-0 ${status.isOpen ? "text-green-500" : "text-red-400"}`} />
        <span className={status.isOpen ? "text-green-600" : "text-red-500"}>{status.statusWord}</span>
        <span className="text-slate-700">{status.isOpen ? `until ${status.nextOpen}` : status.nextOpen}</span>
        {expanded ? <ChevronUp className="w-3.5 h-3.5 text-slate-400 ml-auto" /> : <ChevronDown className="w-3.5 h-3.5 text-slate-400 ml-auto" />}
      </button>
      {expanded && (
        <div className="mt-2 pl-6 space-y-1" onClick={(e) => e.stopPropagation()}>
          {status.weekly.map((d) => (
            <div key={d.day} className={`flex justify-between text-sm ${d.isToday ? "font-bold text-teal" : "text-slate-600"}`}>
              <span className="w-28">{d.day}</span>
              <span>{d.hours}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function LocationsPage() {
  const [locations, setLocations] = useState([]);
  const [comingSoon, setComingSoon] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showMap, setShowMap] = useState(true);
  const [sections, setSections] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchCoords, setSearchCoords] = useState(null);
  const [radius, setRadius] = useState("25");
  const [searching, setSearching] = useState(false);
  const [filteredLocations, setFilteredLocations] = useState(null);
  const [stateFilter, setStateFilter] = useState("All");

  useEffect(() => {
    Promise.all([
      getLocations(true),
      getComingSoonLocations().catch(() => []),
      getPageContent("locations").catch(() => []),
    ]).then(([locs, cs, secs]) => {
      setLocations(locs);
      setComingSoon(cs);
      setSections(secs);
    }).catch(console.error).finally(() => setLoading(false));
  }, []);

  const handleSearch = async () => {
    if (!searchQuery.trim()) {
      setSearchCoords(null);
      setFilteredLocations(null);
      return;
    }
    setSearching(true);
    try {
      const res = await fetch(`https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(searchQuery)}&countrycodes=us&limit=1`);
      const data = await res.json();
      if (data.length > 0) {
        const coords = { lat: parseFloat(data[0].lat), lng: parseFloat(data[0].lon), display: data[0].display_name };
        setSearchCoords(coords);
        const r = parseFloat(radius);
        const allLocs = [...locations, ...comingSoon];
        const withDist = allLocs
          .filter(l => l.latitude && l.longitude)
          .map(l => ({ ...l, distance: getDistanceMiles(coords.lat, coords.lng, l.latitude, l.longitude) }))
          .filter(l => l.distance <= r)
          .sort((a, b) => a.distance - b.distance);
        setFilteredLocations(withDist);
      } else {
        setFilteredLocations([]);
        setSearchCoords(null);
      }
    } catch {
      setFilteredLocations(null);
    }
    setSearching(false);
  };

  const clearSearch = () => {
    setSearchQuery("");
    setSearchCoords(null);
    setFilteredLocations(null);
  };

  const hero = getSection(sections, "hero", { title: "Find a Prime Media Wireless Location Near You", content: "Visit one of our locations for expert assistance.", metadata: { subtitle: "Our Locations" } });
  const cta = getSection(sections, "cta", { title: "Want to Open a Location?", content: "Become a Prime Media Wireless dealer.", button_text: "Become a Dealer", button_link: "/become-dealer" });

  const allLocations = [...locations, ...comingSoon];
  const displayLocations = (filteredLocations || allLocations)
    .filter((l) => stateFilter === "All" || (l.state || "").toLowerCase() === stateFilter.toLowerCase())
    .sort((a, b) => {
      // Coming soon always at the end
      if (a.coming_soon && !b.coming_soon) return 1;
      if (!a.coming_soon && b.coming_soon) return -1;
      const stateA = (a.state || "").toLowerCase(), stateB = (b.state || "").toLowerCase();
      if (stateA !== stateB) return stateA.localeCompare(stateB);
      const cityA = (a.city || "").toLowerCase(), cityB = (b.city || "").toLowerCase();
      if (cityA !== cityB) return cityA.localeCompare(cityB);
      return (a.address || "").toLowerCase().localeCompare((b.address || "").toLowerCase());
    });
  const locationsWithCoords = displayLocations.filter((l) => l.latitude && l.longitude);

  // Derive unique states from all locations including coming soon
  const states = [...new Set(allLocations.map((l) => l.state).filter(Boolean))].sort();

  return (
    <PublicLayout>
      <SEOHead
        pageSlug="locations"
        fallback={{
          meta_title: "Find a Total Wireless Store Near You | Prime Media Wireless Locations",
          meta_description: "Find Total Wireless and Prime Media Wireless authorized dealer locations near you. Search by city, state, or zip code.",
          canonical_url: `${window.location.origin}/locations`,
        }}
      />
      <section className="bg-navy py-20" data-testid="locations-hero">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <p className="label-style text-teal mb-4">{hero.metadata?.subtitle}</p>
            <h1 className="font-heading font-bold text-4xl lg:text-5xl text-white mb-6">{hero.title}</h1>
            <p className="text-slate-300 text-lg font-body">{hero.content}</p>
          </div>
        </div>
      </section>

      {/* Search Bar */}
      <section className="bg-white border-b border-slate-200 sticky top-16 z-30" data-testid="locations-search">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && handleSearch()}
                placeholder="Search by city, state, or zip code..."
                className="pl-10 h-11"
                data-testid="location-search-input"
              />
            </div>
            <Select value={radius} onValueChange={(v) => { setRadius(v); if (searchCoords) setTimeout(handleSearch, 0); }}>
              <SelectTrigger className="w-40 h-11" data-testid="radius-select">
                <Navigation className="w-4 h-4 mr-1 text-slate-400" />
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="5">5 miles</SelectItem>
                <SelectItem value="10">10 miles</SelectItem>
                <SelectItem value="25">25 miles</SelectItem>
                <SelectItem value="50">50 miles</SelectItem>
                <SelectItem value="100">100 miles</SelectItem>
                <SelectItem value="250">250 miles</SelectItem>
              </SelectContent>
            </Select>
            <Button onClick={handleSearch} disabled={searching} className="bg-teal hover:bg-teal/90 text-white h-11 px-6" data-testid="location-search-btn">
              {searching ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Search className="w-4 h-4 mr-2" />}
              Search
            </Button>
            {filteredLocations !== null && (
              <Button variant="outline" onClick={clearSearch} className="h-11" data-testid="clear-search-btn">
                Clear
              </Button>
            )}
          </div>
          {filteredLocations !== null && (
            <div className="mt-3 flex items-center gap-2 text-sm" data-testid="search-results-info">
              {searchCoords && (
                <span className="text-slate-500">
                  Showing <strong className="text-navy">{filteredLocations.length}</strong> store{filteredLocations.length !== 1 ? "s" : ""} within <strong className="text-navy">{radius} miles</strong> of <span className="text-teal">{searchCoords.display?.split(",").slice(0, 2).join(",")}</span>
                </span>
              )}
              {!searchCoords && filteredLocations.length === 0 && (
                <span className="text-red-500">No results found. Try a different search or larger radius.</span>
              )}
            </div>
          )}
        </div>
      </section>

      {!loading && locationsWithCoords.length > 0 && showMap && (
        <section className="bg-white border-b border-slate-200" data-testid="locations-map">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-heading font-semibold text-navy text-lg flex items-center gap-2"><Map className="w-5 h-5 text-teal" /> Store Map</h2>
              <button onClick={() => setShowMap(false)} className="text-sm text-slate-500 hover:text-slate-700 font-body">Hide Map</button>
            </div>
            <div className="rounded-xl overflow-hidden shadow-md border border-slate-200" style={{ height: "500px" }}>
              <MapContainer center={[39.8283, -98.5795]} zoom={4} style={{ height: "100%", width: "100%" }} scrollWheelZoom={false}>
                <TileLayer attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>' url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
                <FitBounds locations={locationsWithCoords} searchCoords={searchCoords} />
                {searchCoords && (
                  <>
                    <Marker position={[searchCoords.lat, searchCoords.lng]} icon={redIcon}>
                      <Popup><strong>Search Location</strong></Popup>
                    </Marker>
                    <Circle center={[searchCoords.lat, searchCoords.lng]} radius={parseFloat(radius) * 1609.34} pathOptions={{ color: "#0d9488", fillColor: "#0d9488", fillOpacity: 0.08, weight: 1 }} />
                  </>
                )}
                {locationsWithCoords.map((loc) => (
                  <Marker key={loc.id} position={[loc.latitude, loc.longitude]} icon={loc.coming_soon ? orangeIcon : tealIcon}>
                    <Popup>
                      <div className="font-body">
                        <strong className="font-heading text-sm">{loc.name}</strong>
                        {loc.coming_soon && <span className="ml-1 text-xs text-orange-600 font-bold">Coming Soon</span>}
                        <br />
                        <span className="text-xs">{loc.address}, {loc.city}, {loc.state}</span><br />
                        {!loc.coming_soon && <CallBadge phone={loc.phone} className="mt-1" testId={`map-call-badge-${loc.id}`} />}
                        {loc.distance !== undefined && <><br /><span className="text-xs text-teal font-semibold">{loc.distance.toFixed(1)} miles away</span></>}
                      </div>
                    </Popup>
                  </Marker>
                ))}
              </MapContainer>
            </div>
          </div>
        </section>
      )}

      {!showMap && !loading && locationsWithCoords.length > 0 && (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <button onClick={() => setShowMap(true)} className="text-sm text-teal hover:underline font-body flex items-center gap-1"><Map className="w-4 h-4" /> Show Map</button>
        </div>
      )}

      {/* State Filter Tabs */}
      {!loading && states.length > 0 && (
        <section className="bg-slate-100 border-b border-slate-200" data-testid="state-filter">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
            <h2 className="font-heading font-bold text-2xl lg:text-3xl text-navy text-center mb-2">Location Listings</h2>
            <div className="w-12 h-1 bg-teal mx-auto mb-6 rounded-full"></div>
            <div className="flex flex-wrap justify-center gap-2">
              <button
                onClick={() => setStateFilter("All")}
                className={`px-5 py-2.5 text-sm font-medium transition-all ${stateFilter === "All" ? "bg-navy text-white shadow-md" : "bg-white text-slate-600 hover:bg-slate-50 border border-slate-200"}`}
                data-testid="state-filter-all"
              >
                All
              </button>
              {states.map((state) => (
                <button
                  key={state}
                  onClick={() => setStateFilter(state)}
                  className={`px-5 py-2.5 text-sm font-medium transition-all ${stateFilter === state ? "bg-navy text-white shadow-md" : "bg-white text-slate-600 hover:bg-slate-50 border border-slate-200"}`}
                  data-testid={`state-filter-${state}`}
                >
                  {state}
                </button>
              ))}
            </div>
            {stateFilter !== "All" && (
              <p className="text-center text-sm text-slate-500 mt-3">Showing <strong className="text-navy">{displayLocations.length}</strong> location{displayLocations.length !== 1 ? "s" : ""} in <strong className="text-navy">{stateFilter}</strong></p>
            )}
          </div>
        </section>
      )}

      <section className="py-16 bg-slate-50" data-testid="locations-grid">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {loading ? (
            <div className="text-center py-12"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal mx-auto"></div><p className="text-slate-500 mt-4 font-body">Loading locations...</p></div>
          ) : displayLocations.length === 0 ? (
            <div className="text-center py-12"><MapPin className="w-12 h-12 text-slate-300 mx-auto mb-4" /><p className="text-slate-500 font-body">{filteredLocations !== null ? "No stores found in this area. Try expanding your search radius." : "No locations available at the moment"}</p></div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {displayLocations.map((location, index) => {
                const isComingSoon = location.coming_soon;
                const cardContent = (
                  <Card className={`border-slate-200 hover-lift h-full cursor-pointer group ${isComingSoon ? "border-orange-300 bg-orange-50/30" : ""}`} data-testid={`location-card-${location.id}`}>
                    <CardContent className="p-6">
                      <div className="flex items-start justify-between mb-4">
                        <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${isComingSoon ? "bg-orange-100" : "bg-teal/10"}`}>
                          {isComingSoon ? <Sparkles className="w-6 h-6 text-orange-500" /> : <MapPin className="w-6 h-6 text-teal" />}
                        </div>
                        <div className="flex items-center gap-2">
                          {isComingSoon && (
                            <span className="text-xs font-bold text-orange-600 bg-orange-100 px-3 py-1 rounded-full" data-testid={`coming-soon-badge-${location.id}`}>
                              Coming Soon
                            </span>
                          )}
                          {location.distance !== undefined && (
                            <span className="text-xs font-semibold text-teal bg-teal/10 px-2 py-1 rounded-full" data-testid={`distance-${location.id}`}>
                              {location.distance.toFixed(1)} mi
                            </span>
                          )}
                          {!isComingSoon && <ChevronRight className="w-5 h-5 text-slate-400 group-hover:text-teal transition-colors" />}
                        </div>
                      </div>
                      <h3 className="font-heading font-semibold text-navy text-lg mb-2">{location.name}</h3>
                      <div className="space-y-2 mb-4">
                        <p className="text-slate-600 text-sm font-body flex items-start gap-2"><MapPin className="w-4 h-4 text-slate-400 mt-0.5 flex-shrink-0" />{location.address}, {location.city}, {location.state} {location.zip_code}</p>
                        {!isComingSoon && <CallBadge phone={location.phone} testId={`call-badge-${location.id}`} />}
                        {!isComingSoon && location.hours && <StoreHours hours={location.hours} />}
                      </div>
                      {!isComingSoon && location.services?.length > 0 && (
                        <div className="flex flex-wrap gap-1">
                          {location.services.slice(0, 3).map((service) => (<span key={service} className="px-2 py-0.5 bg-slate-100 text-slate-600 text-xs rounded-full font-body">{service}</span>))}
                          {location.services.length > 3 && <span className="px-2 py-0.5 bg-slate-100 text-slate-600 text-xs rounded-full font-body">+{location.services.length - 3} more</span>}
                        </div>
                      )}
                    </CardContent>
                  </Card>
                );

                return (
                  <motion.div key={location.id} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: index * 0.05 }}>
                    {isComingSoon ? (
                      <div>{cardContent}</div>
                    ) : (
                      <Link to={`/locations/${location.slug || location.id}`}>{cardContent}</Link>
                    )}
                  </motion.div>
                );
              })}
            </div>
          )}
        </div>
      </section>

      <section className="py-16 bg-teal" data-testid="locations-cta">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-heading font-bold text-2xl lg:text-3xl text-white mb-4">{cta.title}</h2>
          <p className="text-white/80 max-w-xl mx-auto mb-6 font-body">{cta.content}</p>
          {cta.button_text && (
            <Link to={cta.button_link || "/become-dealer"} className="inline-flex items-center px-6 py-3 bg-navy hover:bg-navy-dark text-white font-semibold rounded-lg transition-colors" data-testid="locations-become-dealer-btn">
              {cta.button_text}<ChevronRight className="w-5 h-5 ml-2" />
            </Link>
          )}
        </div>
      </section>
    </PublicLayout>
  );
}
